/** Article Service locale video row (list-shaped `videoData`). */
type ArticleServiceLocaleVideoRow = {
    locale?: string;
    cdnFile?: string;
    subtitles?: Record<string, string>;
    isActive?: boolean;
};

/** Legacy flat quiz videoData object. */
type LegacyArticleVideoData = {
    articleId?: number;
    title?: string;
    images?: { short?: string; large?: string };
    videoUrl?: string | null;
    videoHash?: string | null;
    subtitles?: Record<string, string>;
};

export type LinkedArticleRef = {
    articleId?: number;
    videoData?: LegacyArticleVideoData | ArticleServiceLocaleVideoRow[] | unknown;
    images?: unknown;
    headerImage?: unknown;
};

export type UnifiedQuizArticleVideoData = {
    articleId: number;
    title: string;
    videoUrl: string | null;
    videoHash: string | null;
    subtitles: Record<string, string>;
    images: { large: string; short?: string };
};

function parseArticleLevelImages(raw: unknown): { large: string; short?: string } {
    if (raw == null || raw === '') return { large: '' };
    if (typeof raw === 'string') {
        try {
            const o = JSON.parse(raw) as { large?: string; short?: string };
            const short = o?.short ?? '';
            const large = (o?.large ?? short) || '';
            return { large, short };
        } catch {
            return { large: '' };
        }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        const o = raw as { large?: string; short?: string };
        const short = o?.short ?? '';
        const large = (o?.large ?? short) || '';
        return { large, short };
    }
    return { large: '' };
}

function pickLocaleRow(rows: ArticleServiceLocaleVideoRow[]): ArticleServiceLocaleVideoRow | null {
    const active = rows.filter((r) => r.isActive !== false);
    const pool = active.length > 0 ? active : rows;
    return pool.find((r) => r.locale === 'en') ?? pool[0] ?? null;
}

function isLocaleVideoRowArray(v: unknown): v is ArticleServiceLocaleVideoRow[] {
    return (
        Array.isArray(v) &&
        v.length > 0 &&
        typeof v[0] === 'object' &&
        v[0] != null &&
        typeof (v[0] as ArticleServiceLocaleVideoRow).cdnFile === 'string'
    );
}

/**
 * Normalizes ARTICLE_LINKED_SECTION article payload to the flat videoData shape
 * expected by admin ViewQuizVideoContainer (legacy object or Article Service list).
 */
export function normalizeLinkedArticleVideo(
    article: LinkedArticleRef | null | undefined,
    sectionTitle?: string
): UnifiedQuizArticleVideoData | null {
    const articleId = article?.articleId;
    if (articleId == null || typeof articleId !== 'number') {
        return null;
    }
    const vd = article?.videoData;
    if (vd == null) {
        return null;
    }

    const parsedArticleImages = parseArticleLevelImages(article?.images);
    const headerImage = typeof article?.headerImage === 'string' ? article.headerImage : '';

    if (isLocaleVideoRowArray(vd)) {
        const row = pickLocaleRow(vd);
        if (!row?.cdnFile?.trim()) {
            return null;
        }
        const subtitles = row.subtitles != null && typeof row.subtitles === 'object' ? { ...row.subtitles } : {};
        const thumbShort = parsedArticleImages.short ?? '';
        const thumbLarge = parsedArticleImages.large || thumbShort || headerImage;

        return {
            articleId,
            title: sectionTitle ?? '',
            videoUrl: row.cdnFile.trim(),
            videoHash: null,
            subtitles,
            images: {
                large: thumbLarge || '',
                short: thumbShort || undefined,
            },
        };
    }

    if (typeof vd !== 'object' || Array.isArray(vd)) {
        return null;
    }

    const legacy = vd as LegacyArticleVideoData;
    const mergedImages = {
        large:
            legacy.images?.large ||
            legacy.images?.short ||
            parsedArticleImages.large ||
            parsedArticleImages.short ||
            headerImage ||
            '',
        short: legacy.images?.short ?? parsedArticleImages.short,
    };

    return {
        articleId: legacy.articleId ?? articleId,
        title: legacy.title ?? sectionTitle ?? '',
        videoUrl: legacy.videoUrl ?? null,
        videoHash: legacy.videoHash ?? null,
        subtitles: legacy.subtitles != null && typeof legacy.subtitles === 'object' ? { ...legacy.subtitles } : {},
        images: mergedImages,
    };
}
